import type { prepareEnvironment } from '@gmrchk/cli-testing-library';
import type { Mock, SuiteCollector, TestOptions } from 'vitest';
import type { UserConfig } from 'vitest/config';

export type Define<T> = Exclude<T, undefined>;

export type Plugin = Exclude<
  Define<UserConfig['plugins']>[number],
  Promise<any> | null | undefined | false | Array<any>
>;

export type Nomit<T, K extends keyof T> = Omit<T, K>;

export type Partialize<T, K extends keyof T> = Nomit<T, K> &
  Partial<Pick<T, K>>;

export type ArgTests = {
  pathTsd: string;
  index?: number;
  name?: string;
  timeout?: number;
};

export type Fn = (...args: any) => any;

export type PickBy<T, U> = {
  [P in keyof T as T[P] extends U ? P : never]: T[P];
};

export type NotPickBy<T, U> = {
  [P in keyof T as T[P] extends U ? never : P]: T[P];
};

export type PickKeysBy<T, U> = keyof PickBy<T, U>;
export type NotPickKeysBy<T, U> = keyof NotPickBy<T, U>;

export type PickByFn<T> = PickBy<T, (...args: any) => any>;
export type PickByFnKeys<T> = keyof PickByFn<T>;

export type Env = Awaited<ReturnType<typeof prepareEnvironment>>;

// #region type MockFsKeys
export type MockFsKeys =
  | 'writeFile'
  | 'readFile'
  | 'mkdir'
  | 'rm'
  | 'readdir'
  | 'existsSync';
// #endregion

export type Args = Env & {
  mockFs: (...envs: MockFsKeys[]) => boolean;
};

type DefFns = PickByFnKeys<Env>;
type Def<K extends DefFns> = [Parameters<Env[K]>, ReturnType<Env[K]>];

export type Defs = {
  [K in DefFns]: Def<K>;
};

export type ConfigDefs = Pick<
  Env,
  'readFile' | 'writeFile' | 'makeDir' | 'removeDir' | 'ls' | 'exists'
>;

export type BuildMockFn = Mock<
  [config: ConfigDefs, ...envs: MockFsKeys[]],
  void
>;

export type MockFn = (args: Args) => void;
export type FnString = (...args: any) => string;

// #region types ReturnRunIf
export type DescribeFnOptional = (
  invite: string | FnString,
  fn?: MockFn,
  options?: number | TestOptions | undefined,
) => SuiteCollector;

export type DescribeFnOptionalParams = Parameters<DescribeFnOptional>;

export type DescribeFn = (
  invite: string | FnString,
  fn: MockFn,
  options?: number | TestOptions | undefined,
) => SuiteCollector;

export type DescribeFnParams = Parameters<DescribeFn>;

// #region const KEYS
export const SIMPLE_TRUE_KEYS = [
  'only',
  'concurrent',
  'sequential',
  'shuffle',
] as const;
export type SimpleFnTrueKeys = (typeof SIMPLE_TRUE_KEYS)[number];
export function isSimpleTrueFnKey(
  value: unknown,
): value is SimpleFnTrueKeys {
  return SIMPLE_TRUE_KEYS.includes(value as any);
}

export const SIMPLE_FALSE_KEYS = ['todo', 'skip'] as const;
export type SimpleFnFalseKeys = (typeof SIMPLE_FALSE_KEYS)[number];
export function isSimpleFalseFnKey(
  value: unknown,
): value is SimpleFnFalseKeys {
  return SIMPLE_FALSE_KEYS.includes(value as any);
}

export const SIMPLE_SIDE_EFFECT_KEYS = ['only'] as const;
export type SimpleFnSideEffectKeys =
  (typeof SIMPLE_SIDE_EFFECT_KEYS)[number];
export function isSideEffectFnKey(
  value: unknown,
): value is SimpleFnSideEffectKeys {
  return SIMPLE_SIDE_EFFECT_KEYS.includes(value as any);
}

export const SIMPLE_NO_SIDE_EFFECT_KEYS = [
  'sequential',
  'shuffle',
  'concurrent',
  'todo',
  'skip',
] as const;
export type SimpleFnNoSideEffectKeys =
  (typeof SIMPLE_NO_SIDE_EFFECT_KEYS)[number];
export function isNotSideEffectFnKey(
  value: _SimpleFnKeys,
): value is SimpleFnNoSideEffectKeys {
  return SIMPLE_NO_SIDE_EFFECT_KEYS.includes(value as any);
}

export const SIMPLE_INSIDE_EFFECT_KEYS = [
  'shuffle',
  'concurrent',
] as const;
export type SimpleFnInsideEffectKeys =
  (typeof SIMPLE_INSIDE_EFFECT_KEYS)[number];
export function isSimpleInsideEffectFnKey(
  value: unknown,
): value is SimpleFnInsideEffectKeys {
  return SIMPLE_INSIDE_EFFECT_KEYS.includes(value as any);
}

export const COMPLEX_KEYS = ['runIf', 'skipIf'] as const;
export type ComplexFnKeys = (typeof COMPLEX_KEYS)[number];

export const SIMPLE_KEYS = [
  ...SIMPLE_TRUE_KEYS,
  ...SIMPLE_FALSE_KEYS,
] as const;
export type _SimpleFnKeys = (typeof SIMPLE_KEYS)[number];

export type SimpleFnKeys<T extends boolean> =
  NoInfer<T> extends true
    ? _SimpleFnKeys
    : NoInfer<T> extends false
      ? SimpleFnFalseKeys
      : _SimpleFnKeys;
// #endregion

export type ReturnIfTrue = DescribeFn &
  Omit<Record<_SimpleFnKeys, DescribeFn>, 'todo'> & {
    todo: DescribeFnOptional;
  };

export type ReturnIfBoolean = DescribeFnOptional &
  Record<_SimpleFnKeys, DescribeFnOptional>;

export type ReturnIfFalse = {
  (invite: string, fn?: MockFn): SuiteCollector;
  skip: (invite: string, fn?: MockFn) => SuiteCollector;
  todo: (invite: string, fn?: MockFn) => SuiteCollector;
};

export type ReturnIf<T extends boolean> =
  NoInfer<T> extends true
    ? ReturnIfTrue
    : NoInfer<T> extends false
      ? ReturnIfFalse
      : ReturnIfBoolean;
// #endregion
